import React, { useEffect, useRef, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { createResourceLocatorString } from '../../util/routes';
import {
  clampProfileListingsPage,
  hasActiveProfileListingsQuery,
  parseProfileListingsSearch,
  profileListingsQueryKey,
  stringifyProfileListingsSearch,
} from '../../util/profileListingsQuery';
import { getCoralChildren, profileListingsVisibility } from '../../util/profileListingsVisibility';
import { normalizeProfileSearch } from '../../util/ownProfileManageListingsSearch';
import {
  REVIEW_TYPE_OF_PROVIDER,
  REVIEW_TYPE_OF_CUSTOMER,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_YOUTUBE,
  propTypes,
} from '../../util/types';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
  isNotFoundError,
} from '../../util/errors';
import {
  getDetailCustomFieldValue,
  getFieldValue,
  pickCustomFieldProps,
} from '../../util/fieldHelpers';
import {
  getCurrentUserTypeRoles,
  hasPermissionToViewData,
  isUserAuthorized,
} from '../../util/userHelpers';
import { richText } from '../../util/richText';
import { extractYouTubeID } from '../../util/string';
import {
  parseProfileTab,
  profileTabSearch,
  PROFILE_TAB_LISTINGS,
  PROFILE_TAB_PROFILE,
  PROFILE_TAB_REVIEWS_AS_BUYER,
  PROFILE_TAB_REVIEWS_FROM_CUSTOMERS,
} from '../../util/profilePageTabs';
import { isProfileTabUserField } from '../../util/profilePageUserFields';
import { profileContactUsername, xenforoDirectMessageUrl } from '../../util/xenforoUrls';

import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  H4,
  Page,
  AvatarMedium,
  NamedLink,
  ListingCard,
  Reviews,
  LayoutSingleColumn,
  NamedRedirect,
  CustomExtendedDataSection,
  PaginationLinks,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';
import { getSearchResultsCardRenderSizes } from '../SearchPage/SearchResultsPanel/searchResultsPanelHelpers';

import OwnProfileManageListings from './OwnProfileManageListings';
import ProfileContactSection from './ProfileContactSection';
import ProfileListingsToolbar from './ProfileListingsToolbar';
import ProfilePageHeader from './ProfilePageHeader';
import ProfilePoliciesSection from './ProfilePoliciesSection';
import ProfileReviewSummary from './ProfileReviewSummary';
import { reloadProfileListings } from './ProfilePage.duck';
import css from './ProfilePage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 20;

export const AsideContent = props => {
  const { user, displayName, showLinkToProfileSettingsPage } = props;
  return (
    <div className={css.asideContent}>
      <AvatarMedium className={css.avatar} user={user} disableProfileLink />
      <H4 as="h1" className={css.mobileHeading}>
        {displayName ? (
          <FormattedMessage id="ProfilePage.mobileHeading" values={{ name: displayName }} />
        ) : null}
      </H4>
      {showLinkToProfileSettingsPage ? (
        <>
          <NamedLink className={css.editLinkMobile} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
          </NamedLink>
          <NamedLink className={css.editLinkDesktop} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
          </NamedLink>
        </>
      ) : null}
    </div>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

/**
 * Which review sections to show based on user-type roles and loaded reviews.
 * Seller reviews remain visible even when Console marks the user as buy-only.
 */
export const getReviewSectionsVisibility = (
  userTypeRoles,
  reviewsOfProvider,
  reviewsOfCustomer
) => {
  const { customer: isCustomerUserType, provider: isProviderUserType } = userTypeRoles;
  return {
    showProviderSection: isProviderUserType || reviewsOfProvider.length > 0,
    showCustomerSection: isCustomerUserType || reviewsOfCustomer.length > 0,
  };
};

/**
 * Single review list for a top-level profile tab (no nested review sub-tabs).
 */
export const ReviewsTabContent = props => {
  const { reviews = [], queryReviewsError, reviewType } = props;
  const filtered = reviews.filter(r => r.attributes.type === reviewType);

  return (
    <div className={css.reviewsTabContent}>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      {filtered.length > 0 ? (
        <>
          <ProfileReviewSummary reviews={filtered} reviewType={reviewType} />
          <Reviews reviews={filtered} />
        </>
      ) : queryReviewsError ? null : (
        <p className={css.emptyTabMessage}>
          <FormattedMessage id="ProfilePage.noReviews" />
        </p>
      )}
    </div>
  );
};

/**
 * Whether a pickCustomFieldProps entry will render something visible.
 * Mirrors SectionText / SectionMultiEnum / SectionYoutubeVideo null checks.
 */
export const customFieldHasVisibleContent = field => {
  if (!field) {
    return false;
  }
  if (field.schemaType === SCHEMA_TYPE_TEXT) {
    return !!field.text;
  }
  if (field.schemaType === SCHEMA_TYPE_MULTI_ENUM) {
    const hasContent = field.showUnselectedOptions || field.selectedOptions?.length > 0;
    return !!field.heading && !!field.options && hasContent;
  }
  if (field.schemaType === SCHEMA_TYPE_YOUTUBE) {
    return !!field.heading && !!field.videoUrl && !!extractYouTubeID(field.videoUrl);
  }
  return false;
};

export const CustomUserFields = props => {
  const { publicData, metadata, userFieldConfig, intl } = props;

  const shouldPickUserField = fieldConfig =>
    ['public', 'metadata'].includes(fieldConfig?.scope) &&
    fieldConfig?.showConfig?.displayInProfile !== false &&
    isProfileTabUserField(fieldConfig?.key, fieldConfig?.showConfig?.label);
  const propsForCustomFields =
    pickCustomFieldProps(
      { publicData, metadata },
      userFieldConfig,
      'userType',
      shouldPickUserField
    ) || [];

  const pickUserFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, userTypeConfig = {}, showConfig = {} } = config;
    const { limitToUserTypeIds, userTypeIds } = userTypeConfig;
    const userType = publicData.userType;
    const isTargetUserType = !limitToUserTypeIds || userTypeIds.includes(userType);

    const { label, displayInProfile } = showConfig;
    const publicDataValue = getFieldValue(publicData, key);
    const metadataValue = getFieldValue(metadata, key);
    const value = publicDataValue !== null ? publicDataValue : metadataValue;

    if (
      displayInProfile &&
      isTargetUserType &&
      value !== null &&
      isProfileTabUserField(key, label)
    ) {
      const detailValue = getDetailCustomFieldValue(
        enumOptions,
        value,
        schemaType,
        key,
        label,
        intl,
        'ProfilePage'
      );

      return detailValue ? filteredConfigs.concat(detailValue) : filteredConfigs;
    }
    return filteredConfigs;
  };

  const detailFields = (userFieldConfig || []).reduce(pickUserFields, []);
  // Card already titles "Social Media Links" — drop matching TEXT field headings only.
  // Multi-enum / YouTube require a heading to render; nulling theirs would hide content.
  const socialSectionTitle = intl
    .formatMessage({ id: 'ProfilePage.socialTitle' })
    .trim()
    .toLowerCase();
  const socialCustomFields = propsForCustomFields.map(field => {
    if (field.schemaType !== SCHEMA_TYPE_TEXT) {
      return field;
    }
    const heading = typeof field.heading === 'string' ? field.heading.trim().toLowerCase() : '';
    return heading && heading === socialSectionTitle ? { ...field, heading: null } : field;
  });
  const hasSocialContent =
    detailFields.length > 0 || socialCustomFields.some(customFieldHasVisibleContent);

  const sectionDetailsProps = {
    ...props,
    fieldConfigs: userFieldConfig,
    heading: null,
    rootClassName: css.userFieldDetails,
  };

  return (
    <section className={classNames(css.sectionCard, css.userFieldSection)}>
      <H4 as="h2" className={css.sectionCardTitle}>
        <FormattedMessage id="ProfilePage.socialTitle" />
      </H4>
      {hasSocialContent ? (
        <CustomExtendedDataSection
          sectionDetailsProps={sectionDetailsProps}
          propsForCustomFields={socialCustomFields}
          idPrefix="profilePage"
          pickExtendedDataFields={pickUserFields}
          rootClassName={css.userFieldDetails}
        />
      ) : (
        <p className={css.socialEmpty}>
          <FormattedMessage id="ProfilePage.socialEmpty" />
        </p>
      )}
    </section>
  );
};

export const MainContent = props => {
  const {
    userShowError,
    bio,
    displayName,
    listings,
    queryListingsError,
    reviews = [],
    queryReviewsError,
    publicData,
    metadata,
    userFieldConfig,
    intl,
    hideReviews,
    activeTab = PROFILE_TAB_PROFILE,
    profileUser,
    contactHref,
    showItemsSold = false,
    itemsSoldCount = 0,
    hideContact = false,
    isCurrentUser = false,
    showOwnManageListings = false,
    profileUserId,
    onManageDisableScrolling = () => {},
    pagination,
    listingsHeadingRef,
    visibility = {},
    coralChildren = [],
    listingsQuery = {},
    onListingsQueryChange,
    pagePathParams,
    pageSearchParams,
    activeListingsCount: activeListingsCountProp,
  } = props;

  const hasListings = listings.length > 0;
  const listingsHeadingCount = pagination?.totalItems ?? 0;
  const activeListingsCount = activeListingsCountProp ?? 0;
  const { showTopDropdown, showCoralDropdown, showSort } = visibility;
  const showListingsToolbar = showTopDropdown || showCoralDropdown || showSort;
  const hasActiveListingsQuery = hasActiveProfileListingsQuery(listingsQuery);
  const showListingsChrome = hasListings || activeListingsCount > 0 || hasActiveListingsQuery;
  const hasBio = !!bio;
  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });
  const showContact = !hideContact && !!contactHref;

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }

  const showProfileTab = activeTab === PROFILE_TAB_PROFILE;
  const showListingsTab = activeTab === PROFILE_TAB_LISTINGS;
  const showReviewsFromCustomers = activeTab === PROFILE_TAB_REVIEWS_FROM_CUSTOMERS;
  const showReviewsAsBuyer = activeTab === PROFILE_TAB_REVIEWS_AS_BUYER;

  const socialFields = displayName ? (
    <CustomUserFields
      publicData={publicData}
      metadata={metadata}
      userFieldConfig={userFieldConfig}
      intl={intl}
    />
  ) : null;

  const policiesSection = profileUser ? (
    <ProfilePoliciesSection
      user={profileUser}
      onManageDisableScrolling={onManageDisableScrolling}
    />
  ) : null;

  const contactProps = {
    user: profileUser,
    displayName,
    contactHref,
    activeListingsCount,
    showItemsSold,
    itemsSoldCount,
  };

  return (
    <div>
      {showProfileTab ? (
        hasBio ? (
          <div className={css.profileTabWithAbout}>
            <div className={css.profileTabMain}>
              <section className={css.aboutSection}>
                <H4 as="h2" className={css.aboutTitle}>
                  <FormattedMessage id="ProfilePage.aboutTitle" />
                </H4>
                <p className={css.bio}>{bioWithLinks}</p>
              </section>
              {policiesSection}
              {socialFields}
            </div>
            {showContact ? <ProfileContactSection variant="aside" {...contactProps} /> : null}
          </div>
        ) : (
          <>
            {showContact ? <ProfileContactSection variant="banner" {...contactProps} /> : null}
            {policiesSection}
            {socialFields}
          </>
        )
      ) : null}

      {showListingsTab && showOwnManageListings ? (
        <OwnProfileManageListings profileId={profileUserId} />
      ) : showListingsTab ? (
        showListingsChrome ? (
          <div className={css.listingsContainer}>
            <H4 as="h2" className={css.listingsTitle} ref={listingsHeadingRef}>
              <FormattedMessage
                id={isCurrentUser ? 'ProfilePage.myListingsTitle' : 'ProfilePage.listingsHeading'}
                values={{ count: listingsHeadingCount }}
              />
            </H4>
            {showListingsToolbar ? (
              <ProfileListingsToolbar
                visibility={visibility}
                current={listingsQuery}
                coralChildren={coralChildren}
                onChange={onListingsQueryChange}
              />
            ) : null}
            {hasListings ? (
              <ul className={css.listings}>
                {listings.map(l => (
                  <li className={css.listing} key={l.id.uuid}>
                    <ListingCard
                      className={css.listingCard}
                      listing={l}
                      showAuthorInfo={false}
                      renderSizes={getSearchResultsCardRenderSizes(false)}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <p className={css.emptyTabMessage}>
                <FormattedMessage
                  id={
                    hasActiveListingsQuery
                      ? 'ProfilePage.noMatchingListings'
                      : 'ProfilePage.noListings'
                  }
                />
              </p>
            )}
            {pagination?.totalPages > 1 ? (
              <div
                className={css.pagination}
                aria-label={intl.formatMessage({ id: 'ProfilePage.screenreader.pagination' })}
              >
                <PaginationLinks
                  pageName="ProfilePage"
                  pagePathParams={pagePathParams}
                  pageSearchParams={pageSearchParams}
                  pagination={pagination}
                />
              </div>
            ) : null}
          </div>
        ) : (
          <p className={css.emptyTabMessage}>
            <FormattedMessage id="ProfilePage.noListings" />
          </p>
        )
      ) : null}

      {showReviewsFromCustomers && !hideReviews ? (
        <ReviewsTabContent
          reviews={reviews}
          queryReviewsError={queryReviewsError}
          reviewType={REVIEW_TYPE_OF_PROVIDER}
        />
      ) : null}

      {showReviewsAsBuyer && !hideReviews ? (
        <ReviewsTabContent
          reviews={reviews}
          queryReviewsError={queryReviewsError}
          reviewType={REVIEW_TYPE_OF_CUSTOMER}
        />
      ) : null}
    </div>
  );
};

/**
 * ProfilePageComponent
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {boolean} props.useCurrentUser - Whether to use the current user
 * @param {propTypes.user|propTypes.currentUser} props.user - The user
 * @param {propTypes.error} props.userShowError - The user show error
 * @param {propTypes.error} props.queryListingsError - The query listings error
 * @param {Array<propTypes.listing|propTypes.ownListing>} props.listings - The listings
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.queryReviewsError - The query reviews error
 * @returns {JSX.Element} ProfilePageComponent
 */
export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);
  const listingsHeadingRef = useRef(null);
  const skipListingsScrollRef = useRef(true);
  const lastListingsKeyRef = useRef(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    scrollingDisabled,
    params: pathParams,
    currentUser,
    useCurrentUser,
    userShowError,
    user,
    pagination,
    publishedListingsCount,
    listingTypeCounts,
    coralChildCounts,
    onReloadListings,
    ...rest
  } = props;
  const isVariant = pathParams.variant?.length > 0;
  const isPreview = isVariant && pathParams.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;
  const isCurrentUser = currentUser?.id && currentUser?.id?.uuid === pathParams.id;
  const showOwnManageListings =
    !!isCurrentUser &&
    !isPreview &&
    parseProfileTab(rest?.location?.search) === PROFILE_TAB_LISTINGS;

  // Stripe's onboarding needs a business URL for each seller, but the profile page can be
  // too empty for the provider at the time they are creating their first listing.
  // To remedy the situation, we redirect Stripe's crawler to the landing page of the marketplace.
  // TODO: When there's more content on the profile page, we should consider by-passing this redirection.
  const searchParams = rest?.location?.search;

  const listingsPageUrl = query =>
    createResourceLocatorString(
      'ProfilePage',
      routeConfiguration,
      { id: pathParams.id },
      Object.fromEntries(
        new URLSearchParams(stringifyProfileListingsSearch({ tab: PROFILE_TAB_LISTINGS, ...query }))
      )
    );

  const showOwnManageListingsRef = useRef(showOwnManageListings);
  showOwnManageListingsRef.current = showOwnManageListings;

  useEffect(() => {
    if (showOwnManageListings || parseProfileTab(searchParams) !== PROFILE_TAB_LISTINGS) {
      return;
    }

    const parsed = parseProfileListingsSearch(searchParams);
    const clamped = clampProfileListingsPage(parsed, pagination);
    const key = profileListingsQueryKey(parsed);

    if (profileListingsQueryKey(clamped) !== key) {
      lastListingsKeyRef.current = profileListingsQueryKey(clamped);
      history.replace(listingsPageUrl(clamped));
      return;
    }

    if (lastListingsKeyRef.current === null) {
      if (pagination == null && !rest.queryListingsError) {
        return;
      }
      lastListingsKeyRef.current = key;
      return;
    }

    if (lastListingsKeyRef.current === key || !onReloadListings) {
      return;
    }

    onReloadListings(pathParams, searchParams, config).then(result => {
      if (showOwnManageListingsRef.current) {
        return;
      }
      const loadedQuery = result?.query || parsed;
      lastListingsKeyRef.current = profileListingsQueryKey(loadedQuery);
      if (profileListingsQueryKey(loadedQuery) !== key) {
        history.replace(listingsPageUrl(loadedQuery));
      }
    });
  }, [searchParams, pagination?.totalPages, showOwnManageListings]);

  const lastOwnSearchRef = useRef(null);
  useEffect(() => {
    if (!showOwnManageListings || !onReloadListings) {
      return;
    }
    // Strip buyer params only. Do not rewrite hideClosed/hideDrafts from currentUser:
    // a save or currentUser.show() can briefly hold the previous preference and
    // history.replace would snap the toggle back.
    const params = new URLSearchParams(
      searchParams && searchParams.startsWith('?') ? searchParams.slice(1) : searchParams || ''
    );
    let strippedBuyerParams = false;
    ['category', 'subcategory', 'sort'].forEach(key => {
      if (params.has(key)) {
        params.delete(key);
        strippedBuyerParams = true;
      }
    });
    if (params.get('tab') !== PROFILE_TAB_LISTINGS) {
      params.set('tab', PROFILE_TAB_LISTINGS);
      strippedBuyerParams = true;
    }
    const nextSearch = `?${params.toString()}`;
    if (strippedBuyerParams) {
      history.replace(
        createResourceLocatorString(
          'ProfilePage',
          routeConfiguration,
          { id: pathParams.id },
          Object.fromEntries(params.entries())
        )
      );
    }
    const canonical = normalizeProfileSearch(nextSearch);
    if (lastOwnSearchRef.current === canonical) {
      return;
    }
    lastOwnSearchRef.current = canonical;
    onReloadListings(pathParams, nextSearch, config);
  }, [
    searchParams,
    showOwnManageListings,
    onReloadListings,
    pathParams,
    config,
    history,
    routeConfiguration,
  ]);

  useEffect(() => {
    if (skipListingsScrollRef.current) {
      skipListingsScrollRef.current = false;
      return;
    }
    if (parseProfileTab(searchParams) === PROFILE_TAB_LISTINGS) {
      listingsHeadingRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
  }, [searchParams]);

  const isStorefront = searchParams
    ? new URLSearchParams(searchParams)?.get('mode') === 'storefront'
    : false;
  if (isStorefront) {
    return <NamedRedirect name="LandingPage" />;
  }

  const profileUser = useCurrentUser ? currentUser : user;
  const { bio, displayName, publicData, metadata } = profileUser?.attributes?.profile || {};
  const { userFields } = config.user;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const isUnauthorizedOnPrivateMarketplace = isPrivateMarketplace && isUnauthorizedUser;
  const hasUserPendingApprovalError = isErrorUserPendingApproval(userShowError);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasNoViewingRightsOnPrivateMarketplace = isPrivateMarketplace && hasNoViewingRightsUser;

  const userTypeRoles = getCurrentUserTypeRoles(config, profileUser);

  const isDataLoaded = isPreview
    ? currentUser != null || userShowError != null
    : hasNoViewingRightsOnPrivateMarketplace
    ? currentUser != null || userShowError != null
    : user != null || userShowError != null;

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  if (!isDataLoaded) {
    return null;
  } else if (!isPreview && isNotFoundError(userShowError)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!isPreview && (isUnauthorizedOnPrivateMarketplace || hasUserPendingApprovalError)) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (!isPreview && hasNoViewingRightsOnPrivateMarketplace && !isCurrentUser) ||
    isErrorNoViewingPermission(userShowError)
  ) {
    // Someone without viewing rights on a private marketplace is trying to
    // view a profile page that is not their own – redirect to NoAccessPage
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  } else if (!isPreview && isForbiddenError(userShowError)) {
    // This can happen if private marketplace mode is active, but it's not reflected through asset yet.
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  } else if (isPreview && mounted && !isCurrentUser) {
    // Someone is manipulating the URL, redirect to current user's profile page.
    return isCurrentUser === false ? (
      <NamedRedirect name="ProfilePage" params={{ id: currentUser?.id?.uuid }} />
    ) : null;
  } else if ((isPreview || isPrivateMarketplace) && !mounted) {
    // This preview of the profile page is not rendered on server-side
    // and the first pass on client-side should render the same UI.
    return null;
  }

  const reviews = rest.reviews || [];
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  const {
    showProviderSection: showProviderReviewsTab,
    showCustomerSection: showCustomerReviewsTab,
  } = getReviewSectionsVisibility(userTypeRoles, reviewsOfProvider, reviewsOfCustomer);

  const activeTab = parseProfileTab(searchParams);
  const listingsQuery = parseProfileListingsSearch(searchParams);
  const coralChildren = getCoralChildren(config.categoryConfiguration);
  const visibility = profileListingsVisibility({
    typeCounts: listingTypeCounts,
    childCounts: coralChildCounts,
    category: listingsQuery.category,
    totalItems: pagination?.totalItems || 0,
  });
  const activeListingsCount = publishedListingsCount ?? 0;
  const listingsSearch = stringifyProfileListingsSearch({
    tab: PROFILE_TAB_LISTINGS,
    ...listingsQuery,
  });
  const pageSearchParams = Object.fromEntries(new URLSearchParams(listingsSearch));
  const onListingsQueryChange = partial => {
    const nextSearch = stringifyProfileListingsSearch({
      tab: PROFILE_TAB_LISTINGS,
      ...listingsQuery,
      ...partial,
    });
    const queryParams = Object.fromEntries(new URLSearchParams(nextSearch));
    history.push(
      createResourceLocatorString(
        'ProfilePage',
        routeConfiguration,
        { id: pathParams.id },
        queryParams
      )
    );
  };
  const showItemsSold = userTypeRoles?.provider === true;
  const itemsSoldCount = typeof metadata?.itemsSoldCount === 'number' ? metadata.itemsSoldCount : 0;
  const contactHref = xenforoDirectMessageUrl(profileContactUsername({ publicData, displayName }));
  const tabLink = tab => ({
    name: 'ProfilePage',
    params: { id: pathParams.id },
    to: { search: profileTabSearch(tab) },
  });

  // This is rendering normal profile page (not preview for pending-approval)
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        mainEntity: {
          '@type': 'Person',
          name: profileUser?.attributes?.profile?.displayName,
        },
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />}>
        <div className={css.pageShell}>
          <ProfilePageHeader
            user={profileUser}
            displayName={displayName}
            activeListingsCount={activeListingsCount}
            showItemsSold={showItemsSold}
            itemsSoldCount={itemsSoldCount}
            showLinkToProfileSettingsPage={mounted && isCurrentUser}
          />

          <div className={css.pageContent}>
            <nav
              className={css.profileTabs}
              aria-label={intl.formatMessage({ id: 'ProfilePage.screenreader.profileTabs' })}
            >
              <NamedLink
                className={classNames(css.profileTab, {
                  [css.profileTabActive]: activeTab === PROFILE_TAB_PROFILE,
                })}
                activeClassName=""
                {...tabLink(PROFILE_TAB_PROFILE)}
              >
                <FormattedMessage id="ProfilePage.tabProfile" />
              </NamedLink>
              <NamedLink
                className={classNames(css.profileTab, {
                  [css.profileTabActive]: activeTab === PROFILE_TAB_LISTINGS,
                })}
                activeClassName=""
                {...tabLink(PROFILE_TAB_LISTINGS)}
              >
                <FormattedMessage id="ProfilePage.tabListings" />
              </NamedLink>
              {!hasNoViewingRightsOnPrivateMarketplace && showProviderReviewsTab ? (
                <NamedLink
                  className={classNames(css.profileTab, {
                    [css.profileTabActive]: activeTab === PROFILE_TAB_REVIEWS_FROM_CUSTOMERS,
                  })}
                  activeClassName=""
                  {...tabLink(PROFILE_TAB_REVIEWS_FROM_CUSTOMERS)}
                >
                  <FormattedMessage
                    id="ProfilePage.tabReviewsFromCustomers"
                    values={{ count: reviewsOfProvider.length }}
                  />
                </NamedLink>
              ) : null}
              {!hasNoViewingRightsOnPrivateMarketplace && showCustomerReviewsTab ? (
                <NamedLink
                  className={classNames(css.profileTab, {
                    [css.profileTabActive]: activeTab === PROFILE_TAB_REVIEWS_AS_BUYER,
                  })}
                  activeClassName=""
                  {...tabLink(PROFILE_TAB_REVIEWS_AS_BUYER)}
                >
                  <FormattedMessage
                    id="ProfilePage.tabReviewsAsBuyer"
                    values={{ count: reviewsOfCustomer.length }}
                  />
                </NamedLink>
              ) : null}
            </nav>

            <MainContent
              {...rest}
              bio={bio}
              displayName={displayName}
              userShowError={userShowError}
              publicData={publicData}
              metadata={metadata}
              userFieldConfig={userFields}
              hideReviews={hasNoViewingRightsOnPrivateMarketplace}
              intl={intl}
              userTypeRoles={userTypeRoles}
              profileUserId={pathParams.id}
              activeTab={activeTab}
              profileUser={profileUser}
              contactHref={contactHref}
              showItemsSold={showItemsSold}
              itemsSoldCount={itemsSoldCount}
              isCurrentUser={!!isCurrentUser}
              showOwnManageListings={showOwnManageListings}
              onManageDisableScrolling={props.onManageDisableScrolling}
              pagination={pagination}
              listingsHeadingRef={listingsHeadingRef}
              visibility={visibility}
              coralChildren={coralChildren}
              listingsQuery={listingsQuery}
              onListingsQueryChange={onListingsQueryChange}
              pagePathParams={{ id: pathParams.id }}
              pageSearchParams={pageSearchParams}
              activeListingsCount={activeListingsCount}
            />

            {contactHref && !isCurrentUser && activeTab !== PROFILE_TAB_PROFILE ? (
              <div className={css.stickyMobileBar}>
                <a
                  className={css.stickyContact}
                  href={contactHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FormattedMessage id="ProfilePage.contactSeller" />
                </a>
              </div>
            ) : null}
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews = [],
    queryReviewsError,
    pagination,
    publishedListingsCount,
    listingTypeCounts,
    coralChildCounts,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  // Show currentUser's data if it's not approved yet
  const isCurrentUser = userId?.uuid === currentUser?.id?.uuid;
  const useCurrentUser =
    isCurrentUser && !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    useCurrentUser,
    user,
    userShowError,
    queryListingsError,
    listings: getMarketplaceEntities(state, userListingRefs),
    reviews,
    queryReviewsError,
    pagination,
    publishedListingsCount,
    listingTypeCounts,
    coralChildCounts,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onReloadListings: (params, search, config) =>
    dispatch(reloadProfileListings(params, search, config)),
});

const ProfilePage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ProfilePageComponent);

export default ProfilePage;
